/*
 * Copyright (c) 2021 - 2025, Ludvig Lundgren and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { APIClient } from "@api/APIClient";
import { FeedKeys } from "@api/query_keys";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { SlideOver } from "@components/panels";
import { NumberFieldWide, PasswordFieldWide, SwitchGroupWide, TextFieldWide } from "@components/inputs";
import { SelectFieldBasic } from "@components/inputs/select_wide";
import { sleep } from "@utils";
import { ImplementationBadges } from "@screens/settings/Indexer";
import { FeedDownloadTypeOptions } from "@domain/constants";
import { UpdateFormProps } from "@forms/_shared";
import { extractCategoryTreeFromCaps, flattenCategoryIds, parseCapabilitiesPayload } from "@utils/caps";

interface InitialValues {
  id: number;
  indexer: IndexerMinimal;
  enabled: boolean;
  type: FeedType;
  name: string;
  url: string;
  api_key: string;
  cookie: string;
  user_agent: string;
  tls_skip_verify: boolean;
  interval: number;
  timeout: number;
  max_age: number;
  categories: number[];
  capabilities: FeedCaps | null;
  settings: FeedSettings;
}

export function FeedUpdateForm({ isOpen, toggle, data}: UpdateFormProps<Feed>) {
  const { t } = useTranslation("settings");
  const feed = data;
  const [isTesting, setIsTesting] = useState(false);
  const [isTestSuccessful, setIsSuccessfulTest] = useState(false);
  const [isTestError, setIsErrorTest] = useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (feed: Feed) => APIClient.feeds.update(feed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FeedKeys.lists() });

      toast.custom((toastInstance) => <Toast type="success" body={t("forms.feed.updated", { name: feed.name })} t={toastInstance} />);
      toggle();
    }
  });

  const onSubmit = (formData: unknown) => mutation.mutate(formData as Feed);

  const deleteMutation = useMutation({
    mutationFn: (feedID: number) => APIClient.feeds.delete(feedID),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FeedKeys.lists() });

      toast.custom((toastInstance) => <Toast type="success" body={t("forms.feed.deleted", { name: feed.name })} t={toastInstance} />);
    }
  });

  const deleteAction = () => deleteMutation.mutate(feed.id);

  const testFeedMutation = useMutation({
    mutationFn: (feed: Feed) => APIClient.feeds.test(feed),
    onMutate: () => {
      setIsTesting(true);
      setIsErrorTest(false);
      setIsSuccessfulTest(false);
    },
    onSuccess: () => {
      toast.custom((toastInstance) => <Toast type="success" body={t("forms.feed.testOk", { name: feed.name })} t={toastInstance} />);

      sleep(1000)
        .then(() => {
          setIsTesting(false);
          setIsSuccessfulTest(true);
        })
        .then(() => {
          sleep(2500).then(() => {
            setIsSuccessfulTest(false);
          });
        });
    },
    onError: () => {
      setIsTesting(false);
      setIsErrorTest(true);
      sleep(2500).then(() => {
        setIsErrorTest(false);
      });
    }
  });

  const testFeed = (data: unknown) => testFeedMutation.mutate(data as Feed);

  const initialValues: InitialValues = {
    id: feed.id,
    indexer: feed.indexer,
    enabled: feed.enabled,
    type: feed.type,
    name: feed.name,
    url: feed.url,
    api_key: feed.api_key,
    cookie: feed.cookie || "",
    user_agent: feed.user_agent || "",
    tls_skip_verify: feed.tls_skip_verify ?? false,
    interval: feed.interval,
    timeout: feed.timeout,
    max_age: feed.max_age,
    categories: feed.categories || [],
    capabilities: feed.capabilities || null,
    settings: {
      ...feed.settings,
      cache_ttl_days: feed.settings?.cache_ttl_days || 31
    }
  };

  return (
    <SlideOver<InitialValues>
      type="UPDATE"
      title={t("forms.feed.title")}
      isOpen={isOpen}
      toggle={toggle}
      onSubmit={onSubmit}
      deleteAction={deleteAction}
      initialValues={initialValues}
      testFn={testFeed}
      isTesting={isTesting}
      isTestSuccessful={isTestSuccessful}
      isTestError={isTestError}
    >
      {(values) => (
        <div>
          <TextFieldWide name="name" label={t("forms.feed.name")} required={true} />

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <div
              className="py-4 flex items-center justify-between space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
              <div>
                <label
                  htmlFor="type"
                  className="block text-sm font-medium text-gray-900 dark:text-white"
                >
                  {t("forms.feed.type")}
                </label>
              </div>
              <div className="flex justify-end sm:col-span-2">
                {ImplementationBadges[feed.type.toLowerCase()]}
              </div>
            </div>

            <div className="py-6 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200">
              <SwitchGroupWide name="enabled" label={t("forms.feed.enabled")} />
            </div>
          </div>
          {values.type === "TORZNAB" && <FormFieldsTorznab feedID={feed.id} />}
          {values.type === "NEWZNAB" && <FormFieldsNewznab feedID={feed.id} />}
          {values.type === "RSS" && <FormFieldsRSS />}
        </div>
      )}
    </SlideOver>
  );
}

function WarningLabel() {
  const { t } = useTranslation("settings");
  return (
    <div className="px-4 py-1">
      <span className="w-full block px-2 py-2 bg-red-300 dark:bg-red-400 text-red-900 dark:text-red-900 text-sm rounded-sm">
        <span className="font-semibold">
          {t("forms.feed.warningTitle")}
        </span>
        <span className="ml-1">
          {t("forms.feed.warningText")}
        </span>
      </span>
    </div>
  );
}

function FormFieldsTorznab({ feedID }: { feedID: number }) {
  const { t } = useTranslation("settings");
  const {
    values: { interval }
  } = useFormikContext<InitialValues>();

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 py-5">
      <TextFieldWide
        name="url"
        label={t("forms.feed.url")}
        help={t("forms.feed.torznabUrlHelp")}
        tooltip={
          <div>
            <p>{t("forms.feed.urlFormatTitle")}</p>
            <br/>
            <ul>
              <li>{t("forms.feed.prowlarr")}: <code className="text-blue-400">http(s)://url.tld/indexerID/api</code></li>
              <li>{t("forms.feed.jackett")}: <code className="text-blue-400">http(s)://url.tld/jackett/api/v2.0/indexers/indexerName/results/torznab/</code></li>
            </ul>
          </div>
        }
      />

      <SelectFieldBasic name="settings.download_type" label={t("forms.feed.downloadType")} options={FeedDownloadTypeOptions} />

      <PasswordFieldWide name="api_key" label={t("forms.feed.apiKey")} />

      <SwitchGroupWide name="tls_skip_verify" label={t("forms.feed.skipTls")} />

      {interval < 15 && <WarningLabel />}
      <NumberFieldWide name="interval" label={t("forms.feed.refreshInterval")} help={t("forms.feed.refreshIntervalHelp")}/>

      <NumberFieldWide name="timeout" label={t("forms.feed.refreshTimeout")} help={t("forms.feed.refreshTimeoutHelp")}/>
      <NumberFieldWide name="max_age" label={t("forms.feed.maxAge")} help={t("forms.feed.maxAgeHelp")}/>
      <NumberFieldWide name="settings.cache_ttl_days" label={t("forms.feed.cacheTTL")} help={t("forms.feed.cacheTTLHelp")}/>

      <FeedCategoriesSection feedID={feedID} />
    </div>
  );
}

function FormFieldsNewznab({ feedID }: { feedID: number }) {
  const { t } = useTranslation("settings");
  const {
    values: { interval }
  } = useFormikContext<InitialValues>();

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 py-5">
      <TextFieldWide
        name="url"
        label={t("forms.feed.url")}
        help={t("forms.feed.newznabUrlHelp")}
        tooltip={
          <div>
            <p>{t("forms.feed.urlFormatTitle")}</p>
            <br/>
            <ul>
              <li>{t("forms.feed.prowlarr")}: <code className="text-blue-400">http(s)://url.tld/indexerID/api</code></li>
              <li>{t("forms.feed.jackett")}: <code className="text-blue-400">http(s)://url.tld/jackett/api/v2.0/indexers/indexerName/results/newznab/</code></li>
            </ul>
          </div>
        }
      />

      <PasswordFieldWide name="api_key" label={t("forms.feed.apiKey")} />

      <SwitchGroupWide name="tls_skip_verify" label={t("forms.feed.skipTls")} />

      {interval < 15 && <WarningLabel />}
      <NumberFieldWide name="interval" label={t("forms.feed.refreshInterval")} help={t("forms.feed.refreshIntervalHelp")}/>

      <NumberFieldWide name="timeout" label={t("forms.feed.refreshTimeout")} help={t("forms.feed.refreshTimeoutHelp")}/>
      <NumberFieldWide name="max_age" label={t("forms.feed.maxAge")} help={t("forms.feed.maxAgeHelp")}/>
      <NumberFieldWide name="settings.cache_ttl_days" label={t("forms.feed.cacheTTL")} help={t("forms.feed.cacheTTLHelp")}/>

      <FeedCategoriesSection feedID={feedID} />
    </div>
  );
}

function FormFieldsRSS() {
  const { t } = useTranslation("settings");
  const {
    values: { interval }
  } = useFormikContext<InitialValues>();

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 py-5">
      <TextFieldWide
        name="url"
        label={t("forms.feed.url")}
        help={t("forms.feed.rssUrlHelp")}
      />

      <SelectFieldBasic name="settings.download_type" label={t("forms.feed.downloadType")} options={FeedDownloadTypeOptions} />

      <SwitchGroupWide name="tls_skip_verify" label={t("forms.feed.skipTls")} />

      {interval < 15 && <WarningLabel />}
      <NumberFieldWide name="interval" label={t("forms.feed.refreshInterval")} help={t("forms.feed.refreshIntervalHelp")}/>
      <NumberFieldWide name="timeout" label={t("forms.feed.refreshTimeout")} help={t("forms.feed.refreshTimeoutHelp")}/>
      <NumberFieldWide name="max_age" label={t("forms.feed.maxAge")} help={t("forms.feed.maxAgeHelp")}/>
      <NumberFieldWide name="settings.cache_ttl_days" label={t("forms.feed.cacheTTL")} help={t("forms.feed.cacheTTLHelp")}/>

      <PasswordFieldWide name="cookie" label={t("forms.feed.cookie")} help={t("forms.feed.cookieHelp")} />

      <TextFieldWide name="user_agent" label={t("forms.feed.userAgent")} help={t("forms.feed.userAgentHelp")} />
    </div>
  );
}

function FeedCategoriesSection({ feedID }: { feedID: number }) {
  const { t } = useTranslation("settings");
  const { values, setFieldValue } = useFormikContext<InitialValues>();
  const capsPayload = useMemo(() => parseCapabilitiesPayload(values.capabilities), [values.capabilities]);
  const categoriesTree = useMemo(() => extractCategoryTreeFromCaps(capsPayload), [capsPayload]);
  const hasCaps = Boolean(values.capabilities);

  const fetchCapsMutation = useMutation({
    mutationFn: () => APIClient.feeds.fetchCaps(feedID),
    onSuccess: (caps) => {
      const nextCategories = flattenCategoryIds(extractCategoryTreeFromCaps(caps));
      const selected = values.categories ?? [];

      setFieldValue("capabilities", caps ?? null);
      setFieldValue(
        "categories",
        selected.filter((id) => nextCategories.includes(id))
      );
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : t("forms.feed.fetchFailed");
      toast.custom((toastInstance) => <Toast type="error" body={message} t={toastInstance} />);
    }
  });

  const toggleCategory = (id: number) => {
    const selected = values.categories ?? [];
    if (selected.includes(id)) {
      setFieldValue(
        "categories",
        selected.filter((category) => category !== id)
      );
      return;
    }

    setFieldValue("categories", [...selected, id]);
  };

  const toggleParentCategory = (id: number, childIds: number[]) => {
    const selected = values.categories ?? [];
    if (selected.includes(id)) {
      setFieldValue(
        "categories",
        selected.filter((category) => category !== id)
      );
      return;
    }

    setFieldValue(
      "categories",
      [...selected.filter((category) => !childIds.includes(category)), id]
    );
  };

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700">
      <div className="pt-4 px-4 flex items-center justify-between">
        <div>
          <div className="text-lg font-medium text-gray-900 dark:text-white">{t("forms.feed.categoriesTitle")}</div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("forms.feed.categoriesDescription")}
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200 shadow-xs hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          onClick={() => fetchCapsMutation.mutate()}
          disabled={fetchCapsMutation.isPending}
        >
          {fetchCapsMutation.isPending ? t("forms.feed.fetching") : hasCaps ? t("forms.feed.refetch") : t("forms.feed.fetch")}
        </button>
      </div>

      {categoriesTree.length ? (
        <div className="px-4 pt-4 pb-2 space-y-3 overflow-y-auto">
          {categoriesTree.map((category) => {
            const childIds = category.subcategories.map((sub) => sub.id);
            const isParentSelected = (values.categories ?? []).includes(category.id);

            return (
              <div key={category.id} className="space-y-2">
                <label
                  className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-200"
                  onClick={(event) => event.stopPropagation()}
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={(values.categories ?? []).includes(category.id)}
                      onChange={() => toggleParentCategory(category.id, childIds)}
                      onClick={(event) => event.stopPropagation()}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                    />
                    <span className="font-medium truncate">{category.name}</span>
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{category.id}</span>
                </label>

                {category.subcategories.map((subCategory) => (
                  <label
                    key={subCategory.id}
                    className="flex items-center justify-between gap-3 pl-6 text-sm text-gray-700 dark:text-gray-200"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={(values.categories ?? []).includes(subCategory.id)}
                        onChange={() => toggleCategory(subCategory.id)}
                        onClick={(event) => event.stopPropagation()}
                        disabled={isParentSelected}
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800"
                      />
                      <span className="truncate">{subCategory.name}</span>
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{subCategory.id}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4 pt-3 pb-2 text-sm text-gray-500 dark:text-gray-400">
          {hasCaps ? t("forms.feed.noCategories") : t("forms.feed.fetchToSelect")}
        </div>
      )}
    </div>
  );
}
